import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Radar, AlertTriangle, Clock, ChevronRight, Calendar } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function urgencyColor(days) {
  if (days === null || days === undefined) return { border: "rgba(148,163,184,0.4)", text: "#94a3b8", bg: "rgba(148,163,184,0.1)" };
  if (days <= 7) return { border: "rgba(239,68,68,0.6)", text: "#fca5a5", bg: "rgba(239,68,68,0.15)" };
  if (days <= 30) return { border: "rgba(245,158,11,0.55)", text: "#fcd34d", bg: "rgba(245,158,11,0.12)" };
  return { border: "rgba(59,173,45,0.4)", text: "#86efac", bg: "rgba(59,173,45,0.08)" };
}

export function GrantsRadarInicioWidget() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    fetch(`${BACKEND_URL}/api/grants/radar/inicio-widget`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { setItems(Array.isArray(d) ? d.slice(0, 3) : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading || !items.length) return null;

  const mostUrgent = items[0]?.days_until_deadline;
  const isCritical = mostUrgent !== null && mostUrgent !== undefined && mostUrgent <= 7;

  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18 }}
      data-testid="grants-radar-widget"
      style={{
        marginBottom: 40,
        borderRadius: 20,
        padding: 24,
        background: isCritical
          ? "linear-gradient(135deg, rgba(239,68,68,0.10) 0%, rgba(245,158,11,0.06) 100%)"
          : "linear-gradient(135deg, rgba(59,173,45,0.08) 0%, rgba(37,99,235,0.05) 100%)",
        border: isCritical ? "1px solid rgba(239,68,68,0.28)" : "1px solid rgba(59,173,45,0.18)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <Radar size={16} color={isCritical ? "#fca5a5" : "#86efac"} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: isCritical ? "#fca5a5" : "#86efac" }}>
              Radar · Deadlines más próximos
            </span>
          </div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: "#f1f5f9", margin: 0 }}>
            {isCritical ? "🔥 Cierran esta semana" : "🎯 Convocatorias abiertas"}
          </h3>
        </div>
        <button
          onClick={() => navigate("/grants")}
          data-testid="grants-radar-widget-open"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
            background: "rgba(59,173,45,0.15)", color: "#86efac", border: "1px solid rgba(59,173,45,0.28)",
            borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}
        >
          Ver radar completo <ChevronRight size={14} />
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
        {items.map((c) => {
          const u = urgencyColor(c.days_until_deadline);
          const source = c.source === "cdti" ? "CDTI" : "EU";
          const sourceColor = c.source === "cdti" ? { bg: "rgba(59,173,45,0.18)", text: "#86efac" } : { bg: "rgba(37,99,235,0.18)", text: "#93c5fd" };
          return (
            <button
              key={c.id}
              onClick={() => navigate("/grants")}
              data-testid={`grants-radar-widget-item-${c.id}`}
              style={{
                textAlign: "left", padding: 16, borderRadius: 14,
                background: "rgba(15,23,42,0.5)", border: `1px solid ${u.border}`,
                color: "#f1f5f9", cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: sourceColor.bg, color: sourceColor.text }}>
                  {source}
                </span>
                <span style={{ fontSize: 11, color: "rgba(241,245,249,0.6)", fontWeight: 600 }}>{c.program}</span>
                <span
                  style={{
                    marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                    background: u.bg, color: u.text, border: `1px solid ${u.border}`,
                  }}
                >
                  {c.days_until_deadline !== null && c.days_until_deadline <= 7
                    ? <AlertTriangle size={11} />
                    : <Clock size={11} />}
                  {c.days_until_deadline !== null && c.days_until_deadline !== undefined
                    ? `${c.days_until_deadline}d`
                    : "TBD"}
                </span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35, marginBottom: 8, minHeight: 36 }}>
                {c.title}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "rgba(241,245,249,0.5)" }}>
                <Calendar size={11} />
                Cierre: {c.deadline_text || (c.deadline ? new Date(c.deadline).toLocaleDateString("es-ES") : "TBD")}
              </div>
            </button>
          );
        })}
      </div>
    </motion.section>
  );
}
